import { useState } from 'react';
import { CheckCircle2, Circle, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InvoiceRunErrorKind } from '@/graphql/generatedTypes';
import { Routes } from '@/utils/routes';
import { cn } from '@/lib/utils';

import type { InvoiceRunDetailQuery } from './InvoiceRunDetail.generated';
import { useResolveInvoiceRunErrorMutation } from './ResolveInvoiceRunError.generated';
import { useUnresolveInvoiceRunErrorMutation } from './UnresolveInvoiceRunError.generated';
import { formatCount, formatRelativeDay } from './invoiceRunPresentation';

type RunDetail = NonNullable<InvoiceRunDetailQuery['invoiceRun']>;
type RunError = RunDetail['errors'][number];

const KIND_LABEL: Record<InvoiceRunErrorKind, string | null> = {
  [InvoiceRunErrorKind.Store]: null,
  [InvoiceRunErrorKind.FinalInventory]: 'Final inventory',
  [InvoiceRunErrorKind.Collection]: 'Collection',
};

/**
 * entity_id is the colugo text id of the store (both store kinds) or of the
 * invoice collection — the id both admin pages take in their route.
 */
const entityHref = (error: RunError): string => {
  switch (error.kind) {
    case InvoiceRunErrorKind.Collection:
      return Routes.invoiceCollection(error.entity_id);
    default:
      return Routes.partnerStore(error.entity_id);
  }
};

function ErrorRow({
  error,
  onToggled,
}: {
  error: RunError;
  onToggled: () => void;
}) {
  const [resolve] = useResolveInvoiceRunErrorMutation();
  const [unresolve] = useUnresolveInvoiceRunErrorMutation();
  const [busy, setBusy] = useState(false);

  const resolved = error.resolved_at != null;
  const name = error.entity_name ?? error.entity_id;
  const kindLabel = KIND_LABEL[error.kind];

  const toggle = async () => {
    setBusy(true);
    try {
      if (resolved) {
        await unresolve({ variables: { id: error.id } });
        toast.success(`Reopened ${name}`);
      } else {
        // The mutation accepts an optional note; the design has no note input.
        await resolve({ variables: { id: error.id } });
        toast.success(`${name} marked resolved`);
      }
      onToggled();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Updating the row failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        'tw:grid tw:grid-cols-[minmax(170px,1fr)_118px_minmax(240px,1.6fr)_128px] tw:items-start tw:gap-x-3 tw:border-b tw:border-border tw:px-4 tw:py-2.5 tw:last:border-b-0',
        resolved && 'tw:bg-muted',
      )}
    >
      <div className="tw:flex tw:flex-col tw:gap-1">
        {/* Opens in a new tab: working an error means looking at the store
            while keeping this list open. */}
        <a
          href={entityHref(error)}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'tw:group tw:flex tw:w-fit tw:items-center tw:gap-1 tw:text-sm tw:font-medium tw:no-underline tw:hover:underline',
            resolved ? 'tw:text-muted-foreground' : 'tw:text-foreground',
          )}
        >
          {name}
          <ExternalLink className="tw:size-3 tw:shrink-0 tw:opacity-0 tw:transition-opacity tw:group-hover:opacity-100" />
        </a>
        {kindLabel && (
          <span className="tw:w-fit tw:rounded-full tw:bg-muted tw:px-1.5 tw:py-0.5 tw:text-[11px] tw:font-medium tw:text-muted-foreground">
            {kindLabel}
          </span>
        )}
      </div>
      <span className="tw:break-all tw:font-mono tw:text-xs tw:text-muted-foreground">
        {error.entity_id}
      </span>
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        <span
          className={cn(
            'tw:text-sm tw:whitespace-normal',
            resolved ? 'tw:text-muted-foreground' : 'tw:text-foreground',
          )}
        >
          {error.message}
        </span>
        {resolved && (
          <span className="tw:text-xs tw:text-emerald-600 tw:dark:text-emerald-400">
            Resolved{error.resolved_by ? ` by ${error.resolved_by}` : ''}
            {error.resolved_at
              ? ` · ${formatRelativeDay(error.resolved_at)}`
              : ''}
          </span>
        )}
      </div>
      <div className="tw:flex tw:justify-end">
        {resolved ? (
          <Button
            variant="ghost"
            size="sm"
            className="tw:h-7 tw:text-emerald-600 tw:hover:text-emerald-700 tw:dark:text-emerald-400"
            disabled={busy}
            onClick={toggle}
          >
            <CheckCircle2 className="tw:size-3.5" />
            Resolved
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="tw:h-7"
            disabled={busy}
            onClick={toggle}
          >
            <Circle className="tw:size-3.5" />
            Mark resolved
          </Button>
        )}
      </div>
    </div>
  );
}

export function ErrorsCard({
  run,
  onChanged,
  onDownloadErrorsCsv,
}: {
  run: RunDetail;
  onChanged: () => void;
  /** Present when the run's result files contain an errors file. */
  onDownloadErrorsCsv?: (() => void) | null;
}) {
  const errors = run.errors;
  if (errors.length === 0) return null;

  const total = errors.length;
  const resolved = errors.filter((e) => e.resolved_at != null).length;

  return (
    <Card className="tw:gap-0 tw:py-0">
      <CardHeader className="tw:flex tw:flex-row tw:items-start tw:justify-between tw:gap-3 tw:px-4 tw:py-4">
        <div className="tw:flex tw:flex-col tw:gap-1">
          <CardTitle className="tw:text-base">Stores that failed</CardTitle>
          <p className="tw:text-sm tw:text-muted-foreground">
            {resolved > 0
              ? `${formatCount(resolved)} of ${formatCount(total)} handled manually in Billomat. Everything else in this run was invoiced normally.`
              : 'Everything else in this run was invoiced normally. Mark a row resolved once you have handled it in Billomat.'}
          </p>
        </div>
        {onDownloadErrorsCsv && (
          <Button variant="outline" size="sm" onClick={onDownloadErrorsCsv}>
            <Download className="tw:size-3.5" />
            errors.csv
          </Button>
        )}
      </CardHeader>
      <CardContent className="tw:px-0 tw:pb-0">
        <div className="tw:overflow-x-auto tw:border-t tw:border-border">
          <div className="tw:min-w-[700px]">
            {errors.map((error) => (
              <ErrorRow key={error.id} error={error} onToggled={onChanged} />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
